import posthog from 'posthog-js';
import { resetPosthog } from './posthog.shared';

let init = false;

export type InitPostHogClientProps = {
  key: string;
  distinctId?: string;
};

const onPostHogClientInitListeners = new Set<() => void>();

/**
 * This function should never be called directly, only via `usePosthogInitClient()`
 */
export function initPostHogClient({ key, distinctId }: InitPostHogClientProps) {
  let runListeners = false;
  if (typeof window !== 'undefined') {
    if (!init && key) {
      posthog.init(key, {
        api_host: 'https://ph.findigs.com',
        ui_host: 'https://us.posthog.com',
        defaults: '2025-05-24',
        advanced_only_evaluate_survey_feature_flags: true,
        autocapture: false,
      });
      init = true;
      runListeners = true;
    }

    if (init && distinctId) {
      posthog.identify(distinctId);
    }
  }

  if (runListeners) {
    for (const listener of onPostHogClientInitListeners) {
      listener();
    }
  }
}

export function onPostHogClientInit(listener: () => void) {
  onPostHogClientInitListeners.add(listener);
}

export function offPostHogClientInit(listener: () => void) {
  onPostHogClientInitListeners.delete(listener);
}

/** True once posthog marks the session identified via $user_state. */
export function isPosthogIdentified(): boolean {
  if (!init) {
    return false;
  }

  // reset() rotates distinct_id but keeps $device_id; never compare them.
  return (
    posthog.get_property('$user_state') === 'identified' ||
    Boolean(posthog._isIdentified?.())
  );
}

/** Resets a prior identified user on pre-auth mounts; keeps anon id intact. */
export function resetPosthogIfIdentified() {
  if (isPosthogIdentified()) {
    resetPosthog();
  }
}

/** Resets if identified — immediately when initialized, else after init. */
export function resetPosthogIfIdentifiedOnInit() {
  if (init) {
    resetPosthogIfIdentified();
    return;
  }
  onPostHogClientInit(resetPosthogIfIdentified);
}

/**
 * This function should never be called directly, only via `usePosthogClient()`
 */
export function getPostHogClient() {
  if (!init) return null;

  return posthog;
}

export type PostHogClient = typeof posthog;
